<template lang="pug">
#other-user-permitted-function-list(v-cloak)
  // 使用機能の目次
  .box.box-margin
    .box-header.with-border.register-header
      .title
        span 施設関係者の使用機能
      .buttons
        button.btn.edit-btn(v-if="_showEditButton" @click="gotoPermittedFunctionEdit" type="button") 使用機能の変更
    .box-body.register-body
      .message.mb-2(v-if="_showEditButton")
        span 各機能の使用設定を変更したい場合は、右上の使用機能の変更からご変更ください。
      .message.mb-2(v-else)
        span 各機能の使用設定を変更したい場合は、管理者ユーザーで設定変更してください。
      // 目次を表示する
      .function-list.d-flex.flex-column.align-items-center
        .content.d-flex.flex-row.align-items-center(v-for="item in permittedFunctionItems")
          .icon.d-flex.flex-column.align-items-center
            img(:src="item.iconUrl")
            span.used(v-if="item.permitted == true") 使用中
            span.unused(v-else) 未使用
          .function.flex-grow-1
            .title {{ item.title }}
            .text {{ item.text }}
          .buttons.d-flex.flex-row-reverse
            button.btn.link-btn(v-scroll-to="item.linkUrl") 機能詳細
  // 使用機能の詳細説明
  .box
    .box-body.register-body
      .function-detail-list.d-flex.flex-column.align-items-center
        .content.d-flex.flex-column(v-for="item in permittedFunctionItems" :id="item.id")
          // アイコンとタイトル
          .icon.d-flex.align-items-center
            img.icon-image(:src="item.iconUrl")
            span.title {{ item.title }}
          // 詳細説明のコンテンツは改行や文字色を部分的に変更するなど、自由にデザインを作りたいのでidで分岐して自由にhtmlを記載する
          .description
            // 施設関係者からの連絡
            .opinion(v-if="item.id == 'otherUserOpinion'")
              .d-flex.flex-row
                .text.flex-grow-1
                  span
                    strong ■ 特徴
                    ul
                      li
                        span 施設関係者は
                        span.key-phrase いつでも簡単
                        span にLINEから施設に連絡できます。
                  span
                    strong ■ 機能
                    ul
                      li
                        span 施設関係者は連絡機能を使用して、施設に連絡を送ることが出来ます。
                      li
                        span 管理システムの受信メッセージで施設関係者からの連絡を確認し、返信することが出来ます。
                    span ※ 施設関係者は通常のLINEのトークではなく、HitomeQコネクトの連絡機能を使用します。
          // 上に戻るボタン
          .buttons.d-flex.flex-row-reverse
            button.btn.link-btn(v-scroll-to="'#permitted-function-list'") 上に戻る

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { Getter } from "vuex-class";

import { PermittedFunctions } from "@/commons/services/Facilities";
import { PcUrl } from "@/commons/constants/PcUrl";
import { Account } from "@/commons/services/Authorization";

import PageLoading from "@/pc/basics/PageLoading.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";

/**
 * 使用機能の確認画面コンポーネント
 */
@Component({
  components: {
    FormLabel,
    PageLoading
  }
})
export default class OtherUserPermittedFunctionList extends Vue {
  /** ユーザー名。ユーザー情報修正画面で更新されるので、storeのgetterから値を取得する */
  @Getter("user") user!: Account;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 機能使用可否 */
  permittedFunctions: PermittedFunctions = {
    careimage: false,
    opinion: false,
    reservation: false,
    onlineReservation: false,
    outing: false,
    picture: false,
    event: false,
    otherUserOpinion: false,
    waiterOpinion: false
  };

  /** 機能使用可否各行の情報 */
  permittedFunctionItems: Array<OtherUserPermittedFunctionListItem> = [];

  get _showEditButton(): boolean {
    return userStore.user.permissions.functions.useOtherUserFunctionsFunction;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 機能の使用可否をstoreから取得する
    this.permittedFunctions = userStore.facility.permittedFunctions;

    // 機能の使用可否から目次の情報を作成する
    // 画像のURL等、保持していない情報が多いのでfor文等は使用できない
    this.permittedFunctionItems = [
      {
        id: "otherUserOpinion",
        permitted: this.permittedFunctions.otherUserOpinion,
        iconUrl: require("../../../assets/permitted-function-opinion-icon.png"),
        title: "施設関係者からの連絡",
        text: "施設関係者から施設に連絡・問い合わせが出来ます。施設では内容の確認と返信が出来ます。",
        linkUrl: "#other-user-opinion"
      }
    ];

    this.isBusy = false;

    return;
  }

  /** 施設関係者の使用機能の変更画面へ遷移する */
  public gotoPermittedFunctionEdit(): void {
    this.$router.push(PcUrl.OtherUserPermittedFunctionEdit);
    return;
  }
}

interface OtherUserPermittedFunctionListItem {
  /** ページ内リンク用のID */
  id: string;
  /** 機能の使用可否 */
  permitted: boolean;
  /** アイコン画像URL */
  iconUrl: string;
  /** 機能名 */
  title: string;
  /** 機能説明文 */
  text: string;
  /** ページ内リンクURL */
  linkUrl: string;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

// 親要素からネストしてスタイルを指定する
#other-user-permitted-function-list {
  max-width: 980px;
  margin-left: auto;
  margin-right: auto;

  .edit-btn {
    width: 160px;
  }

  .message {
    padding: 0 0 10px 10px;
  }

  // 使用機能の目次
  .function-list {
    width: 100%;

    .content {
      width: 100%;
      padding-top: 5px;
      padding-bottom: 5px;
      padding-left: 11px;
      padding-right: 11px;
      margin-bottom: 15px;
      border-bottom: solid 1px #cccccc;
      // アイコン
      .icon {
        min-width: 60px;
        width: 60px;
        margin-right: 12px;
        // 使用中・未使用
        .used,
        .unused {
          font-weight: bold;
        }
        .used {
          color: $font-key-color;
        }
        .unused {
          color: $font-disable-font-color;
        }
      }
      // 機能名
      .function {
        margin-right: 12px;
        .title {
          font-size: 18px;
          font-weight: bold;
        }
      }
      // 機能詳細ボタン
      .buttons {
        width: 110px;

        .link-btn {
          width: 100px;
        }
      }
    }
    .content:last-child {
      border-bottom: 0;
      margin-bottom: 0;
    }
  }

  // 使用機能の詳細説明
  .function-detail-list {
    width: 100%;

    .content {
      width: 100%;
      padding-top: 0px;
      padding-bottom: 5px;
      padding-left: 11px;
      padding-right: 11px;
      margin-bottom: 15px;
      border-bottom: solid 1px #cccccc;
      // アイコンとタイトル
      .icon {
        margin-bottom: 12px;

        .icon-image {
          margin-right: 12px;
        }
        .title {
          font-size: 18px;
          font-weight: bold;
        }
      }
      // 詳細説明のコンテンツ
      .description {
        margin-bottom: 15px;
        // 画像
        .images {
          width: 200px;
          margin-right: 15px;

          img {
            width: 200px;
          }
        }
        // 詳細説明テキスト
        .text {
          .key-phrase {
            font-weight: bold;
            color: $font-key-color;
          }
        }
      }
      // 上に戻るボタン
      .buttons {
        width: 100%;
        margin-bottom: 7px;
      }
    }
    .content:last-child {
      border-bottom: 0;
      margin-bottom: 0;
    }
  }
}
</style>
